/**
 * 插入 CAD 预览下载边界。
 *
 * 设计说明：
 * 1. 插入零部件恢复为 development 的“先下载并预览、确认后挂载”流程；
 * 2. 最新后端不再提供 `CadService.InsertCadFile`，因此下载阶段直接按文件路径读取；
 * 3. 下载 URL 必须使用 `/download/file?file_id=...`，缺少可用 `fileId` 时直接失败，避免按 UUID 猜测路径。
 */
import type { FileInfo } from '@puppycad/proto'
import {
  refreshVersionFileAuthorizationHeader,
  resolveVersionFileAuthorizationHeader,
} from '@/services/api/cad-session'
import { AuthReplaySession } from '@/services/auth-replay'
import { apiConfig } from '@/services/api/config'
import { logger } from '@/utils'

const ALLOWED_INSERT_TYPES = ['cad_part', 'cad_assembly'] as const
type InsertCadType = (typeof ALLOWED_INSERT_TYPES)[number]

export interface DownloadInsertedCadFileRequest {
  /** 当前 CAD 会话 ID；保留字段用于兼容旧调用签名。 */
  sessionId: string
  /** 用户选择的 CAD 文件路径，会作为 `/download/file` 的 `file_id` 参数。 */
  projectRootPath: string
  /** 插入目标类型，用于保留零件 / 装配体语义校验。 */
  type: InsertCadType
}

export interface DownloadInsertedCadFileResult {
  /** 根 GLB 文件描述，供通用导入链路作为当前文件使用。 */
  glbFile: FileInfo
  /** `/download/file` 不依赖 UUID，保留空字符串兼容旧结构。 */
  backendGlbFileUuid: string
  /** sidecar JSON 文件描述列表；路径下载入口不返回 sidecar 清单。 */
  sidecarFiles: FileInfo[]
  /** 插入链路可见的资产文件列表。 */
  projectFiles: FileInfo[]
  /** 下载到的根 GLB 字节。 */
  bytes: Uint8Array
}

/**
 * 去除后端根地址尾部多余斜杠。
 *
 * @param backendBaseUrl 后端服务根地址。
 * @returns 可安全拼接下载路径的后端根地址。
 */
function trimBackendBaseUrl(backendBaseUrl: string): string {
  const backend = backendBaseUrl.trim().replace(/\/+$/u, '')
  if (!backend) {
    throw new Error('downloadInsertedCadFile: backendBaseUrl 不能为空')
  }
  return backend
}

/**
 * 归一化插入下载使用的文件路径。
 *
 * @param fileId 外部传入的文件路径。
 * @returns 去除首尾空白并统一分隔符后的文件路径。
 */
function normalizeInsertedDownloadFileId(fileId: string): string {
  return fileId.trim().replace(/\\/g, '/').replace(/^\.\/+/, '')
}

/**
 * 断言插入下载路径存在。
 *
 * @param fileId 待校验的文件路径。
 * @param context 错误定位上下文。
 */
function assertInsertedDownloadFileId(fileId: string, context: string): void {
  if (!fileId.trim()) {
    throw new Error(`${context}: 缺少可用于 /download/file 的 fileId，无法下载插入零部件文件`)
  }
}

/**
 * 构造 `/download/file` 下载地址。
 *
 * @param fileId 后端文件路径。
 * @returns 可交给 fetch 的下载 URL。
 */
export function buildInsertedFileDownloadUrl(fileId: string): string {
  const normalizedFileId = normalizeInsertedDownloadFileId(fileId)
  assertInsertedDownloadFileId(normalizedFileId, 'buildInsertedFileDownloadUrl')
  return `${trimBackendBaseUrl(apiConfig.baseUrl)}/download/file?file_id=${encodeURIComponent(normalizedFileId)}`
}

/**
 * 读取 HTTP 响应体字节。
 *
 * @param response fetch 响应。
 * @returns 响应体字节。
 */
async function readHttpResponseBytes(response: Response): Promise<Uint8Array> {
  return new Uint8Array(await response.arrayBuffer())
}

/**
 * 执行一次插入文件 HTTP 下载。
 *
 * @param fileId 后端文件路径。
 * @param authorizationHeader Authorization header。
 * @returns 下载结果；401 会以 unauthorized 标记返回给外层刷新 token。
 */
async function downloadInsertedFileByFileIdOnce(
  fileId: string,
  authorizationHeader: string,
): Promise<{ readonly bytes?: Uint8Array; readonly unauthorized: boolean }> {
  const normalizedFileId = normalizeInsertedDownloadFileId(fileId)
  assertInsertedDownloadFileId(normalizedFileId, 'downloadInsertedFileByFileId')

  const response = await fetch(buildInsertedFileDownloadUrl(normalizedFileId), {
    headers: {
      Authorization: authorizationHeader,
    },
  })

  if (response.status === 401) {
    return { unauthorized: true }
  }

  if (!response.ok) {
    throw new Error(
      `downloadInsertedFileByFileId: /download/file 下载失败，fileId=${normalizedFileId}, status=${response.status}`,
    )
  }

  return {
    bytes: await readHttpResponseBytes(response),
    unauthorized: false,
  }
}

/**
 * 通过 `/download/file?file_id=...` 下载插入链路文件。
 *
 * @param fileId 后端文件路径。
 * @returns 文件字节。
 */
export async function downloadInsertedFileByFileId(fileId: string): Promise<Uint8Array> {
  const normalizedFileId = normalizeInsertedDownloadFileId(fileId)
  assertInsertedDownloadFileId(normalizedFileId, 'downloadInsertedFileByFileId')

  let authorizationHeader = await resolveVersionFileAuthorizationHeader()
  const replaySession = new AuthReplaySession({
    requestKind: 'version-file',
    operation: normalizedFileId,
    fileId: normalizedFileId,
    url: buildInsertedFileDownloadUrl(normalizedFileId),
  })

  while (true) {
    const result = await downloadInsertedFileByFileIdOnce(normalizedFileId, authorizationHeader)
    if (!result.unauthorized) {
      if (!result.bytes) {
        throw new Error(`downloadInsertedFileByFileId: 响应缺少文件字节，fileId=${normalizedFileId}`)
      }
      return result.bytes
    }

    replaySession.recordUnauthorizedAndAssertCanReplay({
      status: 401,
      cause: new Error(`downloadInsertedFileByFileId: 下载认证失败，fileId=${normalizedFileId}`),
    })

    authorizationHeader = await replaySession.refreshAuthorization(refreshVersionFileAuthorizationHeader)
  }
}

/**
 * 创建插入链路内部使用的 FileInfo。
 *
 * @param fileId 后端文件路径。
 * @param type 用户选择的 CAD 类型。
 * @returns 可交给现有 loader 消费的 FileInfo。
 */
function createInsertedGlbFileInfo(fileId: string, type: InsertCadType): FileInfo {
  const normalizedFileId = normalizeInsertedDownloadFileId(fileId)
  assertInsertedDownloadFileId(normalizedFileId, 'createInsertedGlbFileInfo')
  const displayName = normalizedFileId.split('/').pop() || normalizedFileId

  return {
    fileId: normalizedFileId,
    uuid: `insert:${normalizedFileId}`,
    kind: '.glb',
    displayName: displayName || type,
  } as unknown as FileInfo
}

/**
 * 解析插入 CAD 文件地址并下载根文件。
 *
 * @param request 插入 CAD 下载请求。
 * @returns 根文件字节与参与本次导入的文件清单。
 */
export async function downloadInsertedCadFile(
  request: DownloadInsertedCadFileRequest,
): Promise<DownloadInsertedCadFileResult> {
  if (!ALLOWED_INSERT_TYPES.includes(request.type)) {
    throw new Error(
      `downloadInsertedCadFile: type must be cad_part or cad_assembly, got ${request.type}`,
    )
  }

  const normalizedFileId = normalizeInsertedDownloadFileId(request.projectRootPath)
  assertInsertedDownloadFileId(normalizedFileId, 'downloadInsertedCadFile')

  logger.info('[insert-cad-download] 通过 /download/file 下载插入零部件文件', {
    projectRootPath: normalizedFileId,
    type: request.type,
  })

  const glbFile = createInsertedGlbFileInfo(normalizedFileId, request.type)
  const bytes = await downloadInsertedFileByFileId(normalizedFileId)

  return {
    glbFile,
    backendGlbFileUuid: '',
    sidecarFiles: [],
    projectFiles: [glbFile],
    bytes,
  }
}
